const prfx = (base: string) => (sub: string) => base + sub;

const timeAttendance = prfx("/time-attendance");
const documents = prfx("/documents");
const leaveManagement = prfx("/leave-management");
const assets = prfx("/assets");
const payroll = prfx("/payroll");
const auth = prfx("/auth");

const company = prfx("/company");
const companyTimeAttendance = prfx(company("/time-attendance"));
const companyLeaveManagement = prfx(company("/leave-management"));
const companyAssets = prfx(company("/assets"));
const companyRegularAssets = prfx(companyAssets("/regular-assets"));
const companyConsumableAssets = prfx(companyAssets("/consumable-assets"));
const companyEmployees = prfx(company("/employees"));

const companyPayroll = prfx(company("/payroll"));

const companySettings = prfx(company("/settings"));
const companyOrgSettings = prfx(companySettings("/organization-settings"));
const companyShiftSettings = prfx(companySettings("/shift"));
const companyCompensationSettings = prfx(companySettings("/compensation"));
const companyLeavesSettings = prfx(companySettings("/leaves"));
const companyTimeAttendanceSettings = prfx(companySettings("/time-attendance"));
const companyRolesSettings = prfx(companySettings("/roles"));

const settings = prfx("/settings");
const profileSettings = prfx(settings("/profile-settings"));
const personalInformation = prfx(profileSettings("/personal-information"));
const employmentInformation = prfx(profileSettings("/employment-information"));
const bankingDetails = prfx(profileSettings("/banking-details"));

export const ROUTES = {
  HOME: {
    ROOT: "/",
    TIME_ATTENDANCE: {
      ROOT: timeAttendance("/"),
      REGULARIZATION: timeAttendance("?tab=regularization"),
      ADD_REGULARIZATION: timeAttendance("/add-regularization"),
      UPDATE_REGULARIZATION: (id: string | number) =>
        timeAttendance(`/edit-regularization/${id}`),
    },
    LEAVES: {
      ROOT: leaveManagement("/"),
      MODIFY_REQUEST: (id: number) => leaveManagement(`/${id}`),
      APPLY: leaveManagement("/apply"),
    },
    ASSETS: {
      ROOT: assets("/"),
      NEW_REQUEST: assets("/request"),
      REQUEST_DETAILS: assets("/request/:requestId"),
    },
    PAYROLL: {
      ROOT: payroll("/"),
      PAYSLIP: (id: string | number) => payroll(`/payslips/${id}`),
      DISPUTES: payroll("/disputes"),
      DISPUTE_DETAIL: (id: string | number) => payroll(`/disputes/${id}`),
    },
    DOCUMENTS: documents("/"),
    PERFORMANCES: "/performances",
    PROFILE: "/admin/profile",
    AUTH: {
      LOGIN: auth("/login"),
      SIGNUP: auth("/signup"),
      FORGOT_PASSWORD: auth("/forgot-password"),
      PUBLIC_AUTH: auth("/public-auth"),
      RESET_PASSWORD: auth("/reset-password"),
      OTP: auth("/otp"),
      CREATE_DEPARTMENT: auth("/department"),
      ERROR: auth("/error"),
      SUCCESS: auth("/success"),
    },
    PUBLIC: {
      SIGN: "/documents/sign",
      CERTIFICATE: "/documents/sign/letter",
    },
    COMPANY: {
      DASHBOARD: company("/"),
      LIST: company("/list"),
      ADD_COMPANY: company("/add-company"),
      TIME_ATTENDANCE: {
        ROOT: companyTimeAttendance("/"),
        REGULARIZATION: companyTimeAttendance("?tab=regularize-requests"),
        ADD_REGULARIZATION: companyTimeAttendance("/add-regularization"),
        UPDATE_REGULARIZATION: (id: string | number) =>
          companyTimeAttendance(`/add-regularization/${id}`),
      },
      DEPARTMENTS: {
        ROOT: company("/departments"),
        ADD_DEPARTMENT: company("/departments/add"),
      },
      PAYROLL: {
        ROOT: companyPayroll(""),
        SUMMARY: companyPayroll("/summary"),
        DISPUTES: companyPayroll("/disputes"),
        DISPUTE_DETAIL: (id: string | number) =>
          companyPayroll(`/disputes/${id}`),
        PAY_SLIPS: companyPayroll("/pay-slips"),
        PAYMENT_FILES: companyPayroll("/payment-files"),
        FINAL_SETTLEMENTS: companyPayroll("/final-settlements"),
        FINAL_SETTLEMENT_DETAIL: (id: string | number) =>
          companyPayroll(`/final-settlements/${id}`),
        GENERATE: companyPayroll("/generate"),
        RUN: (id: string | number) => companyPayroll(`/run/${id}`),
        RUN_ITEM: (runId: string | number, itemId: string | number) =>
          companyPayroll(`/run/${runId}/items/${itemId}`),
      },
      MARKETPLACE: {
        ROOT: company("/marketplace"),
      },
      FILES: {
        ROOT: "/company/files",
      },
      OFFBOARDING: {
        ROOT: "/company/offboarding",
        DETAIL: (id: number) => `/company/offboarding/${id}`,
      },
      ONBOARDING: {
        ROOT: "/company/onboarding",
      },
      NFC_FORMS: {
        ROOT: "/company/nfc",
      },
      CANDIDATES: {
        ROOT: "/candidates",
        ADD_CANDIDATE: "/company/candidates/add-candidate",
      },
      LEAVE_REQUESTS: {
        ROOT: companyLeaveManagement(""),
        ADD_REQUEST: companyLeaveManagement("/add-request"),
        VIEW_REQUEST: (id: number) => companyLeaveManagement(`/${id}`),
      },
      ASSETS: {
        ROOT: companyAssets(""),
        SINGLE_ASSET_TYPE_LIST: (assetTypeId: number) =>
          companyRegularAssets(`/${assetTypeId}`),
        SINGLE_ASSET_DETAILS: (assetTypeId: number, assetId: number) =>
          companyRegularAssets(`/${assetTypeId}/${assetId}`),
        SINGLE_ASSET_EDIT: (assetTypeId: number, assetId: number) =>
          companyRegularAssets(`/${assetTypeId}/${assetId}/edit`),
        CREATE_ASSET: companyAssets("/create"),
        CREATE_CONSUMABLE_ASSET: companyConsumableAssets("/create"),
        CONSUMABLE_ASSET_DETAILS: (consumableAssetId: number) =>
          companyConsumableAssets(`/edit/${consumableAssetId}`),
        ASSET_REQUEST_DETAILS: (requestId: number) =>
          companyAssets(`/asset-request/${requestId}`),
      },
      LEAVES: {
        ROOT: companyLeaveManagement(""),
      },
      SETTINGS: {
        ROOT: companySettings(""),
        ORGANIZATION_SETTINGS: companyOrgSettings(""),
        COMPANY_INFORMATION: companyOrgSettings("/company-information"),
        BRANCHES: {
          ROOT: companyOrgSettings("/branches"),
          ADD: companyOrgSettings("/branches/add"),
        },
        COMPANY_POLICIES: companyOrgSettings("/company-policies"),
        DEPARTMENTS: {
          ROOT: companyOrgSettings("/departments"),
          ADD: companyOrgSettings("/departments/add"),
        },
        ORGANIZATIONAL_STRUCTURE: companyOrgSettings(
          "/organizational-structure",
        ),
        POSITIONS: {
          ROOT: companyOrgSettings("/positions"),
          ADD: companyOrgSettings("/positions/add"),
        },
        ROLES_PERMISSIONS: {
          ROOT: companyRolesSettings(""),
          ADD_ROLE: companyRolesSettings("/add"),
        },
        TIME_ATTENDANCE: {
          ROOT: companyTimeAttendanceSettings(""),
          BREAK_POLICY: companyTimeAttendanceSettings("/break-policy"),
          ADD_BREAK_POLICY: companyTimeAttendanceSettings("/break-policy/add"),
          CHECK_IN: companyTimeAttendanceSettings("/check-in"),
          RULES: companyTimeAttendanceSettings("/rules"),
          BIOMETRIC: companyTimeAttendanceSettings("/biometric"),
          ADD_BIOMETRIC: companyTimeAttendanceSettings("/biometric/add"),
          KIOSK: companyTimeAttendanceSettings("/kiosk"),
          ADD_KIOSK: companyTimeAttendanceSettings("/kiosk/add"),
        },
        SHIFT: {
          ROOT: companyShiftSettings(""),
          ADD_SHIFT: companyShiftSettings("/add"),
          EDIT_SHIFT: (id: number | string) => companyShiftSettings(`/${id}`),
        },
        COMPENSATION: {
          ROOT: companyCompensationSettings(""),
          ADD_COMPENSATION: companyCompensationSettings("/add"),
          DEDUCTIONS: {
            ROOT: companyCompensationSettings("/deductions"),
            ADD: companyCompensationSettings("/deductions/add"),
          },
          BENEFITS: {
            ROOT: companyCompensationSettings("/benefits"),
            ADD: companyCompensationSettings("/benefits/add"),
          },
        },
        LEAVES: {
          ROOT: companyLeavesSettings(""),
          TYPES: {
            ROOT: companyLeavesSettings("/types"),
            ADD: companyLeavesSettings("/types/add"),
          },
          ALLOWANCES_BENEFITS: {
            ROOT: companyLeavesSettings("/allowance-benefits"),
            ADD: companyLeavesSettings("/allowance-benefits/add"),
          },
          ENTITLEMENTS: {
            ROOT: companyLeavesSettings("/entitlements"),
            ADD: companyLeavesSettings("/entitlements/add"),
          },
          CARRY_FORWARD_POLICY: {
            ROOT: companyLeavesSettings("/carry-policy"),
            ADD: companyLeavesSettings("/carry-policy/add"),
          },
          ENCASHMENT_POLICY: {
            ROOT: companyLeavesSettings("/encashment-policy"),
            ADD: companyLeavesSettings("/encashment-policy/add"),
          },
          APPROVAL_WORKFLOW: {
            ROOT: companyLeavesSettings("/approval-workflow"),
            ADD: companyLeavesSettings("/approval-workflow/add"),
          },
          RULE_RESTRICTIONS: companyLeavesSettings("/rule-restriction"),
          POLICY_VALIDITY: companyLeavesSettings("/policy-validity"),
          BLACKOUT_PERIODS: {
            ROOT: companyLeavesSettings("/blackout-period"),
            ADD: companyLeavesSettings("/blackout-period/add"),
          },
        },
      },
      ANNOUNCEMENT: {
        ROOT: company("/announcements"),
      },
    },
    EMPLOYEES: {
      ROOT: companyEmployees(""),
      ONBOARDING: companyEmployees("/onboarding"),
      VIEW: companyEmployees("/view"),
    },
    PROJECT_BONUSES: {
      ROOT: "/project-bonuses",
    },
    CHATS: {
      ROOT: "/chat",
    },
    AI_ASSISTANT: {
      ROOT: "/ai-assistant",
    },
    SETTINGS: {
      ROOT: settings(""),
      PROFILE_SETTING: {
        ROOT: profileSettings(""),
        PERSONAL_INFORMATION: personalInformation(""),
        EDIT: personalInformation("/edit"),
        EMPLOYMENT_INFORMATION_EDIT: employmentInformation("/edit"),
        EMPLOYMENT_INFORMATION: employmentInformation(""),
        BANKING_DETAILS: bankingDetails(""),
        BANKING_DETAILS_EDIT: bankingDetails("/edit"),
      },
      SECURITY_SETTING: settings("/security-settings"),
    },
    NOTIFICATIONS: {
      ROOT: "/notifications",
    },
    ANNOUNCEMENT: {
      ROOT: "/announcements",
    },
  },
};
